import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { deleteTemplate } from "../services/templatesAPI";
import { useAuth } from "../hooks/useAuth";
import ProjectUserSelect from "./ProjectUserSelect";
import SpecSectionSelect from "./SpecSectionSelect";
import SimpleRichTextEditor from "./SimpleRichTextEditor";

const TemplateModal = ({
  visible,
  onClose,
  onSave,
  editingTemplate,
  templateType = "submittal", // "submittal" | "rfi" | "punch_list_item"
  onTemplateDeleted,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const isSequentialWorkflow =
    selectedProject?.rfi_workflow_type === "Sequential";
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = React.useState(false);

  useEffect(() => {
    if (visible) {
      if (editingTemplate) {
        // Populate form with existing template data
        if (templateType === "punch_list_item") {
          form.setFieldsValue({
            name: editingTemplate.name,
            item_title: editingTemplate.item_title || "",
          });
        } else {
          const formData = {
            name: editingTemplate.name,
            days_duration: editingTemplate.days_duration,
            received_from_id: editingTemplate.received_from_id,
            responsible_contractor_id:
              editingTemplate.responsible_contractor_id,
            submitted_for: editingTemplate.submitted_for || "For Approval",
            priority: editingTemplate.priority,
            distribution_list: editingTemplate.distribution_list,
          };
          // Add type-specific fields
          if (templateType === "submittal") {
            formData.specification_section_id =
              editingTemplate.specification_section_id;
            formData.submittal_manager_id =
              editingTemplate.submittal_manager_id;
            formData.description = editingTemplate.description || "";
            // Add steps only for submittal templates
            if (editingTemplate.steps) {
              formData.steps = editingTemplate.steps.map((step) => ({
                reviews: step.reviews.map((review) => review.reviewer_id),
              }));
            }
          } else if (templateType === "rfi") {
            if (isSequentialWorkflow) {
              if (editingTemplate.steps) {
                formData.steps = editingTemplate.steps.map((step) => ({
                  reviews: step.reviews.map((review) => review.reviewer_id),
                }));
              }
            } else {
              formData.assignee_ids = editingTemplate.assignee_ids || [];
            }
          }

          form.setFieldsValue(formData);
        }
      } else {
        form.resetFields();
        if (templateType !== "punch_list_item") {
          form.setFieldsValue({ priority: "Medium" });
        }
      }
    }
  }, [visible, editingTemplate, form, templateType, isSequentialWorkflow]);

  const handleSubmit = async (values) => {
    setIsSaving(true);
    try {
      if (templateType === "punch_list_item") {
        values.name = values.item_title;
      }
      if (templateType === "submittal" && values.description) {
        const descriptionText = new DOMParser()
          .parseFromString(values.description, "text/html")
          .body.textContent.trim();
        if (!descriptionText) {
          values.description = "";
        }
      }
      await onSave(values);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleDelete = async () => {
    if (!editingTemplate?._id) return;

    Modal.confirm({
      title: "Delete Template",
      content: `Are you sure you want to delete the template "${editingTemplate.name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteTemplate(editingTemplate._id);
          notification.success({ message: "Template deleted successfully" });
          queryClient.invalidateQueries({
            queryKey: ["templates", projectId, templateType],
          });
          if (onTemplateDeleted) {
            onTemplateDeleted();
          }

          handleClose();
        } catch (error) {
          const errorMessage =
            error?.response?.data?.detail?.message ||
            error?.message ||
            "Failed to delete template";
          notification.error({
            message: "Failed to delete template",
            description: errorMessage,
          });
        }
      },
    });
  };

  return (
    <Modal
      title={editingTemplate ? "Edit Template" : "Create Template"}
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        {templateType !== "punch_list_item" && (
          <Form.Item
            label="Template Name"
            name="name"
            rules={[
              { required: true, message: "Please input the template name!" },
            ]}
            className="w-full"
          >
            <Input placeholder="Enter template name" />
          </Form.Item>
        )}

        {templateType === "punch_list_item" && (
          <Form.Item
            label="Title"
            name="item_title"
            rules={[
              {
                required: true,
                message: "Please enter the default item title!",
              },
            ]}
            className="w-full"
          >
            <Input placeholder="Default punch list item title" />
          </Form.Item>
        )}

        {templateType !== "punch_list_item" && (
          <Form.Item
            label="Due in (Days)"
            name="days_duration"
            rules={[
              { required: true, message: "Please input the due in days!" },
              {
                validator: (_, value) => {
                  if (!value || value < 1) {
                    return Promise.reject(
                      new Error("Due must be at least 1 day!"),
                    );
                  }
                  if (value > 365) {
                    return Promise.reject(
                      new Error("Due cannot exceed 365 days!"),
                    );
                  }
                  if (!Number.isInteger(value)) {
                    return Promise.reject(
                      new Error("Due must be a whole number!"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            className="w-full"
          >
            <InputNumber
              min={1}
              max={365}
              step={1}
              precision={0}
              placeholder="Enter due in days"
              className="w-full"
              parser={(value) => value.replace(/\D/g, "")}
              formatter={(value) => (value ? `${value}` : "")}
            />
          </Form.Item>
        )}

        {/* Type-specific fields */}

        {templateType === "submittal" && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Specification Section"
                name="specification_section_id"
                className="w-full"
              >
                <SpecSectionSelect className="w-full" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Submittal Manager"
                name="submittal_manager_id"
                className="w-full"
              >
                <ProjectUserSelect
                  mode={null}
                  allowClear
                  placeholder="Select submittal manager"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {templateType === "submittal" && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Received From"
                name="received_from_id"
                className="w-full"
              >
                <ProjectUserSelect
                  mode={null}
                  allowClear
                  placeholder="Select received from"
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Submitted For"
                name="submitted_for"
                className="w-full"
                initialValue="For Approval"
              >
                <Select
                  options={[
                    { value: "For Approval", label: "For Approval" },
                    { value: "For Record", label: "For Record" },
                  ]}
                  className="w-full"
                  placeholder="Select Submitted For"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {templateType === "submittal" && (
          <Form.Item
            label="Priority"
            name="priority"
            className="w-full"
            initialValue="Medium"
          >
            <Select
              options={[
                { value: "High", label: "High" },
                { value: "Medium", label: "Medium" },
                { value: "Low", label: "Low" },
              ]}
              className="w-full"
            />
          </Form.Item>
        )}

        {templateType === "submittal" && (
          <Form.Item label="Description" name="description" className="w-full">
            <SimpleRichTextEditor
              placeholder="Enter description..."
              aiEnhanceTarget="question"
            />
          </Form.Item>
        )}

        {templateType !== "punch_list_item" && (
          <Form.Item label="Cc" name="distribution_list" className="w-full">
            <ProjectUserSelect
              placeholder="Select Cc"
              className="w-full"
            />
          </Form.Item>
        )}

        {templateType === "submittal" && (
          <Form.List name="steps">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, fieldKey }, index) => (
                  <div key={key} className="mb-4 flex items-center">
                    <Form.Item
                      label={`Step #${index + 1}`}
                      name={[name, "reviews"]}
                      fieldKey={[fieldKey, "reviews"]}
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one reviewer",
                        },
                      ]}
                      className="w-full"
                    >
                      <ProjectUserSelect
                        placeholder="Select reviewers"
                        className="flex-grow"
                      />
                    </Form.Item>
                    <Button
                      type="danger"
                      onClick={() => remove(name)}
                      icon={<CloseOutlined />}
                      className="ml-2"
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  className="mb-4"
                >
                  Add Reviewer
                </Button>
              </>
            )}
          </Form.List>
        )}

        {templateType === "rfi" && !isSequentialWorkflow && (
          <Form.Item
            label="Assignees"
            name="assignee_ids"
            className="w-full"
            rules={[
              {
                validator: (_, value) => {
                  if (!value || value.length === 0) {
                    return Promise.reject(
                      new Error("Please select at least one assignee!"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ProjectUserSelect
              placeholder="Select at least one assignee"
              className="w-full"
            />
          </Form.Item>
        )}

        {templateType === "rfi" && isSequentialWorkflow && (
          <>
            <div className="mb-2 text-sm font-medium text-gray-700">
              Review Steps
            </div>
            <Form.List name="steps">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey }, index) => (
                    <div key={key} className="mb-4 flex items-center">
                      <Form.Item
                        label={`Step #${index + 1}`}
                        name={[name, "reviews"]}
                        fieldKey={[fieldKey, "reviews"]}
                        rules={[
                          {
                            required: true,
                            message: "Please select at least one reviewer",
                          },
                        ]}
                        className="w-full"
                      >
                        <ProjectUserSelect
                          placeholder="Select reviewers"
                          className="flex-grow"
                        />
                      </Form.Item>
                      <Button
                        type="danger"
                        onClick={() => remove(name)}
                        icon={<CloseOutlined />}
                        className="ml-2"
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    className="mb-4"
                  >
                    Add Step
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}

        <Form.Item className="w-full">
          <div className="flex justify-end gap-2">
            <Button onClick={handleClose}>Cancel</Button>
            {editingTemplate && (
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={isSaving}>
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateModal;
